import React, { useState } from 'react';

import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Flex,
  ScrollArea,
  Select,
  Table,
  Text,
  useMantineTheme
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { DeleteContentModal } from 'components/DeleteContentModal';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { useUserContext } from 'contexts/UserContext';
import { InviteUserModal } from 'pages/Organisation/InviteUserModal';
import { OrganisationSectionWrapper } from 'pages/Organisation/OrganisationSectionWrapper';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { OrganisationService } from 'services/organisation-service';
import { UserService } from 'services/user-service';
import { CRUDOperation } from 'shared/constants/crud-operation';
import { USER_UPDATE_NOTIF_ID } from 'shared/constants/notification-ids';
import { ALL_USERS_INFO_QUERY_KEY } from 'shared/constants/query-keys';
import { User, UserAccess } from 'shared/interfaces/User';
import { formatInitials, formatName } from 'shared/utils/format';

const handleError = (error: AxiosError, operation: CRUDOperation) => {
  let failureKey = '';

  switch (operation) {
    case CRUDOperation.UPDATE:
      failureKey =
        error.response?.status == HttpStatusCode.NotFound
          ? 'FEED_BACK.USERS.FAILURE.UPDATE.NOT_FOUND'
          : 'FEED_BACK.USERS.FAILURE.UPDATE.UNKNOWN';
      NotificationService.endAction(false, USER_UPDATE_NOTIF_ID, failureKey, error);
      break;

    case CRUDOperation.DELETE:
      failureKey =
        error.response?.status == HttpStatusCode.NotFound
          ? 'FEED_BACK.USERS.FAILURE.DELETE.NOT_FOUND'
          : 'FEED_BACK.USERS.FAILURE.DELETE.UNKNOWN';
      NotificationService.showResult(false, failureKey, error);
      break;

    default:
      break;
  }
};

export function OrganisationUsersTab() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const roleData = [
    { value: UserAccess.ADMIN.toString(), label: t('USER_ACCESS.ADMIN') },
    { value: UserAccess.MEMBER.toString(), label: t('USER_ACCESS.MEMBER') }
  ];

  const tableHeaders = [
    t('MY_TEAM_TABLE.NAME'),
    t('MY_TEAM_TABLE.EMAIL'),
    t('MY_TEAM_TABLE.STATUS'),
    t('MY_TEAM_TABLE.ROLE'),
    t('APP.ACTIONS.DELETE')
  ];

  const queryClient = useQueryClient();

  const allUsersQuery = useQuery({
    queryKey: [ALL_USERS_INFO_QUERY_KEY],
    queryFn: () => OrganisationService.getAllUsers()
  });

  const [loggedInUser] = useUserContext();
  const [userToDelete, setUserToDelete] = useState<User>();
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState<boolean>(false);

  const updateUserMutation = useMutation({
    mutationFn: (updatedUser: User) => {
      NotificationService.startAction(USER_UPDATE_NOTIF_ID, 'FEED_BACK.USERS.LOADING.UPDATE');
      return OrganisationService.updateUserRole(updatedUser);
    },
    onSuccess: (updatedUser: User) => {
      queryClient.setQueriesData([ALL_USERS_INFO_QUERY_KEY], (existingUsers: User[] = []) =>
        existingUsers.map((user: User) => (user._id === updatedUser._id ? updatedUser : user))
      );

      NotificationService.endAction(true, USER_UPDATE_NOTIF_ID, 'FEED_BACK.USERS.SUCCESS.UPDATE');
    },
    onError: (error: AxiosError) => {
      handleError(error, CRUDOperation.UPDATE);
      allUsersQuery.refetch();
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: OrganisationService.deleteUser,
    onSuccess: (deletedUserId: string) => {
      queryClient.setQueriesData([ALL_USERS_INFO_QUERY_KEY], (existingUsers: User[] = []) =>
        existingUsers.filter((user) => user._id !== deletedUserId)
      );
      NotificationService.showResult(true, 'FEED_BACK.USERS.SUCCESS.DELETE');
      closeDeleteUserModal();
    },
    onError: (error: AxiosError) => {
      handleError(error, CRUDOperation.DELETE);
      allUsersQuery.refetch();
    }
  });

  const setUserRole = (selectedUser: User, selectedUserRole: string | null) => {
    const roleValue = UserService.setOtherUserAccessRole(loggedInUser, selectedUserRole);

    // Our viewer role value is 0, if(!roleValue) is not doable in this case
    if (roleValue === undefined) return;

    if (selectedUser._id === loggedInUser._id) {
      NotificationService.showResult(false, t('MY_TEAM_TABLE.SAME_USER_UNAUTHORIZED'));
      return;
    }

    selectedUser.role = roleValue;
    updateUserMutation.mutate(selectedUser);
  };

  const canEditUser = (fetchedUser: User) => {
    return fetchedUser._id !== loggedInUser._id && UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN);
  };

  // Open/Close modals
  const closeInviteUserModal = () => {
    setIsModalOpened(false);
  };

  const openInviteUserModal = () => {
    setIsModalOpened(true);
  };

  const openDeleteUserModal = (fetchedUser: User) => {
    if (!canEditUser(fetchedUser)) {
      return;
    }
    setUserToDelete(fetchedUser);
    setIsDeleteModalOpened(true);
  };

  const closeDeleteUserModal = () => {
    setIsDeleteModalOpened(false);
  };

  // Renders

  const renderStatusBadge = (status: boolean) => {
    return status ? (
      <Badge color="green" variant="outline" tt="capitalize">
        {t('USER_STATUS.ACTIVE')}
      </Badge>
    ) : (
      <Badge color="gray.6" variant="outline" tt="capitalize">
        {t('USER_STATUS.INACTIVE')}
      </Badge>
    );
  };

  const renderRoleMenu = (user: User) => {
    return user.role === UserAccess.SUPER_ADMIN ? (
      <Text fw="bold" mt={7}>
        {t('MY_TEAM_TABLE.ORG_OWNER')}
      </Text>
    ) : (
      <Select
        disabled={!canEditUser(user)}
        w={150}
        data={roleData}
        value={JSON.stringify(user.role)}
        onChange={(selectValue) => setUserRole(user, selectValue)}
      />
    );
  };

  if (allUsersQuery.isError) return <ErrorAlert />;
  if (allUsersQuery.isLoading) return <FullScreenLoader />;

  return (
    <>
      <OrganisationSectionWrapper
        title={t('ORGANISATION.USERS.SUBTITLE')}
        withMarginBottom
        rightComponent={
          UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN) ? (
            <Button
              leftIcon={<IconPlus />}
              onClick={() => {
                openInviteUserModal();
              }}>
              {t('ORGANISATION.USERS.INVITE_USER')}
            </Button>
          ) : undefined
        }>
        <Flex direction="column" h="100%" style={{ overflowY: 'auto' }}>
          <ScrollArea h="100%" style={{ border: `1px solid ${theme.colors.gray[3]}`, borderRadius: '5px' }}>
            <Table>
              <thead>
                <tr
                  style={{
                    position: 'sticky',
                    zIndex: 2,
                    top: -1,
                    backgroundColor: theme.colors.gray[0]
                  }}>
                  {tableHeaders.map((th, index: number) => {
                    return (
                      <th key={index} style={{ textAlign: index === 0 ? undefined : 'center', width: 'fit-content' }}>
                        {th}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {allUsersQuery.data.map((fetchedUser: User, index: number) => {
                  return (
                    <tr key={index} style={{ textAlign: 'center' }}>
                      <td>
                        <Flex align="center">
                          <Avatar radius={15} size={30} mr="md" src={fetchedUser.avatarPath}>
                            {formatInitials(fetchedUser.firstName, fetchedUser.lastName)}
                          </Avatar>
                          <Text maw={150} truncate>
                            {formatName(fetchedUser.firstName, fetchedUser.lastName)}
                          </Text>
                        </Flex>
                      </td>
                      <td>{fetchedUser.email}</td>
                      <td>{renderStatusBadge(fetchedUser.isActive)}</td>
                      <td style={{ display: 'flex', justifyContent: 'center' }}>{renderRoleMenu(fetchedUser)}</td>

                      <td>
                        <ActionIcon
                          color="red"
                          ml="auto"
                          mr="auto"
                          disabled={!canEditUser(fetchedUser)}
                          onClick={() => {
                            openDeleteUserModal(fetchedUser);
                          }}>
                          <IconTrash />
                        </ActionIcon>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ScrollArea>
        </Flex>
      </OrganisationSectionWrapper>
      <InviteUserModal isModalOpened={isModalOpened} closeModal={closeInviteUserModal} />
      <DeleteContentModal
        isModalOpened={isDeleteModalOpened}
        withCloseButton={false}
        description={t('MY_TEAM_TABLE.DELETE_USER.ARE_YOU_SURE', {
          email: userToDelete?.email
        })}
        alertTitle={t('DELETE_POPUP.ALERT.TITLE')}
        alertDescription={t('MY_TEAM_TABLE.DELETE_USER.DESCRIPTION')}
        loading={deleteUserMutation.isLoading}
        onConfirm={() => {
          if (!userToDelete) return;
          deleteUserMutation.mutate(userToDelete._id);
        }}
        cancelAction={() => {
          closeDeleteUserModal();
        }}
      />
    </>
  );
}
